import React__default, { forwardRef, useRef, useMemo, useImperativeHandle } from 'react';
import { useSensors, useSensor, PointerSensor, DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { Portal, Backdrop, TrapFocus } from '@shopify/polaris';
import classNames from 'classnames';
import { variationName } from '../../utilities/css.mjs';
import { AriaRole } from '../../types.mjs';
import { createRestrictToDroppableRegion, isSheetExpanded } from './utilities.mjs';
import styles from './BottomSheet.scss.mjs';
import { Button } from './components/CompoundComponents/components/Button/Button.mjs';
import { Title } from './components/CompoundComponents/components/Title/Title.mjs';
import { VisuallyHiddenTitle } from './components/CompoundComponents/components/VisuallyHiddenTitle/VisuallyHiddenTitle.mjs';
import { TransitionWrapper } from './components/TransitionWrapper/TransitionWrapper.mjs';
import { useBounceAnimation } from './hooks/useBounceAnimation.mjs';
import { DraggableRegion } from './components/DraggableRegion/DraggableRegion.mjs';
import { CompoundComponentProvider } from './components/CompoundComponents/components/CompoundComponentProvider/CompoundComponentProvider.mjs';
import { DroppableRegion } from './components/DroppableRegion/DroppableRegion.mjs';
import { PanelArea } from '../Frame/components/PanelArea/PanelArea.mjs';

const MS_TO_SECONDS_CONVERSION = 1000;
const SWIPE_VELOCITY = 500;
const DEFAULT_MAX_HEIGHT = 0.85;
const BottomSheetComponent = /*#__PURE__*/forwardRef(function BottomSheet({
  children,
  maxHeight = DEFAULT_MAX_HEIGHT,
  isModal = false,
  fullCollapse = false,
  useFramePortal = false,
  backgroundSheetContent,
  showBackgroundSheet,
  horizontalHeaderPadding = 'base',
  transparentBackdrop = false,
  expansion = 'mid',
  titleTapExpansion = 'mid',
  minHeightCollapsed,
  scrollable = true,
  onExpansionChange,
  ...headerProps
}, ref) {
  const tracked = useRef({
    distance: 0,
    timestamp: 0,
    velocity: 0
  });
  const expanded = isSheetExpanded(expansion);
  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: {
      // Require the pointer to move by 2 pixels before activating
      distance: 2
    }
  }));
  const modifiers = useMemo(() => [restrictToVerticalAxis, createRestrictToDroppableRegion(maxHeight)], [maxHeight]);
  const draggableRegionRef = useRef(null);
  const bounce = useBounceAnimation({
    ref: draggableRegionRef,
    disabled: expanded
  });
  const imperativeMethods = useMemo(() => ({
    bounce
  }), [bounce]);
  useImperativeHandle(ref, () => imperativeMethods);
  const bottomSheetClassNames = classNames(styles.BottomSheet, {
    [styles.fullCollapse]: fullCollapse,
    [styles.expanded]: expanded,
    [styles.isModal]: isModal,
    [styles[variationName('padding', horizontalHeaderPadding)]]: horizontalHeaderPadding
  });
  const bottomSheetInteriorClassNames = classNames(styles.BottomSheetInterior, {
    [styles.isModal]: isModal
  });
  const childrenMarkup = useFramePortal ? /*#__PURE__*/React__default.createElement(PanelArea, null, children) : children;
  const draggableRegionMarkup = /*#__PURE__*/React__default.createElement(DraggableRegion, Object.assign({
    expansion: expansion,
    onTitleTap: titleTapExpansion === 'disabled' ? undefined : handleTitleTap,
    fullCollapse: fullCollapse
    // TODO: combine backgroundSheet props in backgroundSheet object
    // https://github.com/Shopify/online-store-ui/issues/3648
    ,
    backgroundSheetContent: backgroundSheetContent,
    showBackgroundSheet: showBackgroundSheet,
    ref: draggableRegionRef,
    maxHeight: maxHeight,
    minHeightCollapsed: minHeightCollapsed,
    isModal: isModal,
    scrollable: scrollable
  }, headerProps), childrenMarkup);
  const draggableRegionWrapperMarkup = /*#__PURE__*/React__default.createElement(TrapFocus, {
    trapping: expanded && isModal
  }, draggableRegionMarkup);
  const accessibilityState = isModal ? {
    role: AriaRole.Dialog,
    'aria-modal': true
  } : undefined;
  return /*#__PURE__*/React__default.createElement(Portal, null, /*#__PURE__*/React__default.createElement("div", Object.assign({}, accessibilityState, {
    className: bottomSheetClassNames
  }), expanded && isModal ? /*#__PURE__*/React__default.createElement(Backdrop, {
    transparent: transparentBackdrop,
    onClick: handleCloseSheet
  }) : null, /*#__PURE__*/React__default.createElement(CompoundComponentProvider, null, /*#__PURE__*/React__default.createElement(DndContext, {
    autoScroll: false,
    modifiers: modifiers,
    sensors: sensors,
    onDragMove: handleDragMove,
    onDragEnd: handleDragEnd,
    onDragCancel: handleDragCancel
  }, /*#__PURE__*/React__default.createElement("div", {
    style: {
      '--osui_max-droppable-height-bottom-sheet': `${maxHeight * 100}vh`
    },
    className: bottomSheetInteriorClassNames
  }, /*#__PURE__*/React__default.createElement(DroppableRegion, null), draggableRegionWrapperMarkup)))));
  function handleTitleTap() {
    if (titleTapExpansion === 'disabled') {
      return;
    }
    const newExpandedState = expanded ? 'collapsed' : titleTapExpansion;
    onExpansionChange(newExpandedState);
  }
  function handleCloseSheet() {
    onExpansionChange('collapsed');
  }
  function handleDragMove({
    delta
  }) {
    // Track drag velocity
    const timestamp = Date.now();
    const timeDelta = timestamp - tracked.current.timestamp;
    const distance = tracked.current.distance - delta.y;
    const velocity = Math.round(distance / timeDelta * MS_TO_SECONDS_CONVERSION);
    tracked.current = {
      distance: delta.y,
      velocity,
      timestamp
    };
  }
  function handleDragCancel() {
    handleTitleTap();
    resetTrackedRef();
  }
  function handleDragEnd({
    over
  }) {
    const {
      velocity
    } = tracked.current;
    if (Math.abs(velocity) > SWIPE_VELOCITY) {
      setExpansionStateBySwipe(velocity);
    } else if (Math.abs(velocity) < SWIPE_VELOCITY && over) {
      onExpansionChange(over.id);
    }
    resetTrackedRef();
  }
  function resetTrackedRef() {
    tracked.current = {
      distance: 0,
      timestamp: 0,
      velocity: 0
    };
  }
  function setExpansionStateBySwipe(swipeVelocity) {
    const isSwipeUp = swipeVelocity > 0;
    if (isSwipeUp) {
      switch (expansion) {
        case 'collapsed':
        case 'mid':
          onExpansionChange('full');
          break;
      }
    } else {
      switch (expansion) {
        case 'full':
        case 'mid':
          onExpansionChange('collapsed');
          break;
      }
    }
  }
});
const BottomSheetNamespace = Object.assign(BottomSheetComponent, {
  Button,
  Title,
  VisuallyHiddenTitle,
  TransitionWrapper
});

export { BottomSheetNamespace as BottomSheet, BottomSheetComponent };
